import { useState, useCallback, type ReactNode, type HTMLAttributes } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { copyTextToClipboard } from './aidaClipboard';

type CodeBlockProps = HTMLAttributes<HTMLPreElement> & {
  children?: ReactNode;
};

function extractTextFromNode(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromNode).join('');
  if (typeof node === 'object' && 'props' in node) {
    return extractTextFromNode((node as { props?: { children?: ReactNode } }).props?.children);
  }
  return '';
}

function extractLanguage(node: ReactNode): string | null {
  if (!node || typeof node !== 'object') return null;
  if (Array.isArray(node)) {
    for (const child of node) {
      const lang = extractLanguage(child);
      if (lang) return lang;
    }
    return null;
  }
  if (!('props' in node)) return null;
  const className = (node as { props?: { className?: string } }).props?.className ?? '';
  const match = /language-([\w+-]+)/.exec(className);
  return match?.[1] ?? null;
}

export default function CodeBlock({ children, className, ...props }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const codeText = extractTextFromNode(children).replace(/\n$/, '');
  const language = extractLanguage(children);

  const handleCopy = useCallback(async () => {
    const ok = await copyTextToClipboard(codeText);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [codeText]);

  return (
    <div className="aida-code-block">
      <div className="aida-code-block__header">
        <span className="aida-code-block__lang">{language ?? 'code'}</span>
        <button
          type="button"
          className="aida-code-block__copy"
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy code'}
          title={copied ? 'Copied' : 'Copy code'}
        >
          {copied ? <CheckIcon className="h-3.5 w-3.5" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <pre className={className} {...props}>
        {children}
      </pre>
    </div>
  );
}
